import { useEffect } from 'react';
import { UseBoolean } from 'react-hanger';
import { useLocation } from 'react-router';
import cn from 'clsx';
import Constants from '../../../utils/constants';
import { parsePathnameToActiveMenuInfo } from '@components-clean/page-nav/page-nav-menu-active-menu-info';
import styles from '@components-clean/page-nav/page-nav-menu-item.module.scss';
import { PageNavItem } from '@components-clean/page-nav/page-nav-menu-items.data';
import { isStudyMember } from '@utils-clean/studyMember-helper';
import { NullableStudy } from '@contexts/local-state-context';
import { IUser, Permissions } from '@contexts/user-context';
import { useBoolean } from '@hooks/index';
import Modules from '@config/modules';

interface ActiveItemPerLevelInfo {
    topLevel?: { id: string; params?: Record<string, string> };
    secondLevel?: { id: string; params?: Record<string, string> };
    thirdLevel?: { id: string; params?: Record<string, string> };
}

export const getActiveItemOnLevel = (activeItemsInfo: ActiveItemPerLevelInfo, level: number) => {
    if (level === 0) return activeItemsInfo?.topLevel;
    if (level === 1) return activeItemsInfo?.secondLevel;
    return activeItemsInfo?.thirdLevel;
};

const compareParams = (activeItemParams: Record<string, string>, navItemParams: Record<string, string>): boolean => {
    // Find if any params exist in activeItemParams that are different to navItemParams
    return !Object.keys(navItemParams).some((value) => {
        return !Object.hasOwn(activeItemParams, value) || activeItemParams[value] !== navItemParams[value];
    });
};

export const getIsActive = (
    id: string,
    level: number | undefined,
    activeItem: { id: string; params?: Record<string, string> } | undefined,
    activeItemsInfo: ActiveItemPerLevelInfo,
    params: Record<string, string> | undefined,
) => {
    let isActive = activeItem?.id === id;

    if (params) {
        let activeParams: Record<string, string> | undefined;
        switch (level) {
            case 0:
                activeParams = activeItemsInfo.topLevel?.params;
                break;
            case 1:
                activeParams = activeItemsInfo.secondLevel?.params;
                break;
            case 2:
                activeParams = activeItemsInfo.thirdLevel?.params;
                break;
            default:
                activeParams = undefined;
        }
        if (activeParams) {
            isActive = isActive && compareParams(activeParams, params);
        }
    }
    return isActive;
};

interface UsePageNavMenuItemParams {
    activeItemsInfo: ActiveItemPerLevelInfo;
    id: string;
    isPageNavCollapsed?: boolean;
    level?: number;
    params?: Record<string, string>;
    displayMode?: 'default' | 'collapsed';
    itemKey: string | undefined;
    searchActive?: boolean;
    expandOnSearch?: boolean;
    currentPageItemsInfo: ActiveItemPerLevelInfo;
    expandCurrentPageItems: UseBoolean;
}

export const getActiveItemsAfterItemChanged = (
    clickedItemId: string,
    level: number,
    activeItemsInfo: ActiveItemPerLevelInfo,
    params?: Record<string, string>,
): ActiveItemPerLevelInfo => {
    switch (level) {
        case 0:
            return {
                topLevel: activeItemsInfo.topLevel?.id === clickedItemId ? undefined : { id: clickedItemId, params },
                secondLevel: undefined,
                thirdLevel: undefined,
            };
        case 1:
            return {
                topLevel: activeItemsInfo.topLevel,
                secondLevel:
                    activeItemsInfo.secondLevel?.id === clickedItemId ? undefined : { id: clickedItemId, params },
                thirdLevel: undefined,
            };
        case 2:
        default:
            return {
                topLevel: activeItemsInfo.topLevel,
                secondLevel: activeItemsInfo.secondLevel,
                thirdLevel: { id: clickedItemId, params },
            };
    }
};

const checkActiveItemChanged = (currentId: string, activeItemId?: string) =>
    !activeItemId || currentId !== activeItemId;

export const usePageNavMenuItem = ({
    activeItemsInfo,
    id,
    isPageNavCollapsed,
    level = 0,
    params,
    displayMode = 'default',
    itemKey,
    searchActive = false,
    expandOnSearch = false,
    currentPageItemsInfo,
    expandCurrentPageItems,
}: UsePageNavMenuItemParams) => {
    const isCollapsedDisplayMode = displayMode === 'collapsed';
    const isCollapsed = useBoolean(true);

    const isTopLevel = level === 0;
    const isSecondLevel = level === 1;
    const isThirdLevel = level === 2;
    const activeItem = getActiveItemOnLevel(activeItemsInfo, level);
    const activePageItem = getActiveItemOnLevel(currentPageItemsInfo, level);

    const isActive = getIsActive(id, level, activeItem, activeItemsInfo, params);
    const isActivePage = getIsActive(id, level, activePageItem, currentPageItemsInfo, params);

    const location = useLocation();
    const path = location.pathname;
    const levelsKeys = parsePathnameToActiveMenuInfo(path);
    const firstLevelKey = levelsKeys.topLevel?.id;

    const listItemClassNames = !searchActive
        ? cn(styles.listItem, {
              '!k-ml-4 !k-mr-4': !isCollapsedDisplayMode && isTopLevel && !isPageNavCollapsed,
              'k-mx-2': !isCollapsedDisplayMode && isSecondLevel,
              'k-py-2': !isTopLevel,
              'k-font-size-sm': isThirdLevel,
              [styles.activeKeyItem]: (isActive || isActivePage) && (isSecondLevel || isThirdLevel),
              [styles.firstLevelActiveItem]: isTopLevel && (isActive || isActivePage),
              [styles.firstLevelSelectedItem]: isTopLevel && `nav-${firstLevelKey}` === itemKey,
              [styles.secondLevelItem]: isSecondLevel && !isActive,
              [styles.secondLevelActiveItem]: isSecondLevel && (isActive || isActivePage),
              [styles.thirdLevelItem]: isThirdLevel && !isActive,
              [styles.thirdLevelActiveItem]: isThirdLevel && (isActive || isActivePage),
              [styles.topLevelCollapseModeItem]: isCollapsedDisplayMode && isTopLevel,
              [styles.secondLevelCollapseModeItem]: isCollapsedDisplayMode && isSecondLevel,
              [styles.thirdLevelCollapseModeItem]: isCollapsedDisplayMode && isThirdLevel,
              [styles.collapsedMenuListItem]: !isCollapsedDisplayMode && isTopLevel && isPageNavCollapsed,
          })
        : cn(styles.listItem, {
              '!k-ml-4': isTopLevel,
              '!k-mr-4': isTopLevel,
              [styles.firstTierSearchResultsItem]: isTopLevel,
              'k-font-size-sm': isThirdLevel,
              'k-mx-4': isSecondLevel,
              'k-mx-6': isThirdLevel,
              [styles.secondTierSearchResultsItem]: isSecondLevel,
              [styles.thirdTierSearchResultsItem]: isThirdLevel,
          });

    const listItemTextClassNames = cn(styles.listItemText, {
        'k-text-white': isTopLevel || isActive || isActivePage,
        [styles.activeItem]: isActive || isActivePage,
    });

    useEffect(() => {
        const levels = [
            { isLevel: isTopLevel, activeId: activeItemsInfo.topLevel?.id },
            { isLevel: isSecondLevel, activeId: activeItemsInfo.secondLevel?.id },
            { isLevel: isThirdLevel, activeId: activeItemsInfo.thirdLevel?.id },
        ];

        const activeLevel = levels.find((lvl) => lvl.isLevel);

        if (checkActiveItemChanged(id, activeLevel?.activeId)) {
            isCollapsed.setTrue();
        } else {
            isCollapsed.setFalse();
        }

        if (
            isSecondLevel &&
            isActivePage &&
            id === currentPageItemsInfo.secondLevel?.id &&
            expandCurrentPageItems.value
        ) {
            isCollapsed.setFalse();
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [activeItemsInfo]);

    useEffect(() => {
        if (searchActive) {
            if (expandOnSearch) {
                isCollapsed.setFalse();
            } else {
                isCollapsed.setTrue();
            }
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [expandOnSearch]);

    return {
        isActive: isActive || isActivePage,
        isCollapsed,
        classNames: {
            listItem: listItemClassNames,
            listItemText: listItemTextClassNames,
        },
    };
};

export const filterPageNavItems = (
    items: PageNavItem[],
    studyHasLegacyDatasets: boolean,
    studyUsingCM2: boolean,
    pageNavIsCollapsed: boolean,
    user?: IUser | null,
    selectedStudy?: NullableStudy,
    subItem?: boolean | null,
) => {
    if (!user) return [];
    if (user.isSystemAdmin(Constants.SystemAdminLevel.Administrator))
        return [...items]
            .map((el) => ({ ...el }))
            .filter((item) => {
                if (user?.admininstratorCanViewRoute && !user.admininstratorCanViewRoute(item)) return null;

                if (!pageNavIsCollapsed && !item.readOnly) {
                    item.tooltip = undefined;
                }

                if (item.items) {
                    item.items = filterPageNavItems(
                        [...item.items].map((el) => ({ ...el })),
                        studyHasLegacyDatasets,
                        studyUsingCM2,
                        pageNavIsCollapsed,
                        user,
                        selectedStudy,
                    );
                }

                return item;
            })
            .filter((item) => item !== null && (!item.items || item.items.length > 0));
    return [...items]
        .map((el) => ({ ...el }))
        .filter((item) => {
            if (user?.nonAdmininstratorCanViewRoute && !user.nonAdmininstratorCanViewRoute(item)) return null;

            if (item.key === 'study-administration' && !selectedStudy) {
                item.readOnly = true;
            }

            if (item.key === 'organisation-admin' && !user.organisation) {
                item.readOnly = true;
            }

            // Org user checks for hiding, disabling and enabling menu items
            const { moduleValue, element } = item;
            if (moduleValue) {
                // If it requires a module, and we don't have a study, then readonly
                if (!selectedStudy) {
                    item.readOnly = true;
                    item.tooltip = 'common:PageNavTooltips.SelectAStudy';
                    return item;
                }

                if (!isStudyMember(moduleValue, selectedStudy, user)) {
                    item.readOnly = true;
                    item.tooltip = 'common:PageNavTooltips.cannotAccessModule';
                }

                if (
                    moduleValue === Modules.CentralMonitoring.value &&
                    !user.hasModule?.(Modules.CentralMonitoring) &&
                    !user.hasModule?.(Modules.CentralMonitoring2)
                ) {
                    return null;
                }
                if (
                    moduleValue === Modules.CentralMonitoring.value &&
                    user.hasModule?.(Modules.CentralMonitoring2) &&
                    studyUsingCM2 &&
                    !studyHasLegacyDatasets
                ) {
                    return null;
                }
                if (
                    moduleValue === Modules.CentralMonitoring2.value &&
                    !user.hasModule?.(Modules.CentralMonitoring2) &&
                    user.hasModule?.(Modules.CentralMonitoring)
                ) {
                    return null;
                }

                if (
                    (moduleValue === Modules.RiskAssessment.value && !user.hasModule?.(Modules.RiskAssessment)) ||
                    (moduleValue === Modules.CentralMonitoring.value && !user.hasModule?.(Modules.CentralMonitoring)) ||
                    (moduleValue === Modules.CentralMonitoring2.value &&
                        !user.hasModule?.(Modules.CentralMonitoring2)) ||
                    (moduleValue === Modules.SubjectMonitoring.value && !user.hasModule?.(Modules.SubjectMonitoring)) ||
                    (moduleValue === Modules.DataQuality.value && !user.hasModule?.(Modules.DataQuality))
                ) {
                    item.readOnly = true;
                    item.tooltip = 'common:PageNavTooltips.StudyNotConfigured';
                }

                if (
                    element &&
                    !user?.can?.({
                        module: moduleValue,
                        element,
                        permission: Permissions.Enabled,
                    })
                ) {
                    item.readOnly = true;
                }
            }

            if (subItem && !item.readOnly) {
                item.tooltip = undefined;
            }

            // Filter nested items, all the way down
            if (!item.readOnly && item.items) {
                item.items = filterPageNavItems(
                    [...item.items].map((el) => ({ ...el })),
                    studyHasLegacyDatasets,
                    studyUsingCM2,
                    pageNavIsCollapsed,
                    user,
                    selectedStudy,
                    true,
                );
            }

            // Would be nice to only do this for top level items
            if (pageNavIsCollapsed && !item.readOnly) {
                item.tooltip = item.label;
            } else if (!pageNavIsCollapsed && !item.readOnly) {
                item.tooltip = undefined;
            }

            return item;
        })
        .filter((item) => item !== null && (!item.items || item.items.length > 0));
};
